import { DynamicForm, IconButton, OdinIconSize } from '@myosh/odin-components';
import type {
  DataGridRef,
  DialogRef,
  DynamicFormButtonSetting,
  DynamicFormRef,
  JsonDataItem,
} from '@myosh/odin-components';
import { cloneDeep, debounce } from 'lodash';
import { type Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import type { ExtendedDynamicFormSettings } from '../../../@types/forms';
import type { GroupItem, GroupMutationResponse, GroupStructure } from '../../../@types/groups';
import { useApiLogger } from '../../../hooks/use-api-logger';
import useDynamicFormNotifier from '../../../hooks/use-dynamic-form-notifier';
import usePerformingSaveToast from '../../../hooks/use-performing-save-toast';
import { useUserAccess } from '../../../hooks/use-user-access';
import i18next from '../../../i18n';
import { getArchiveRestoreMessages } from '../../../pages/admin/admin-utils';
import { groupApi, useGetGroupByIdQuery, useUpdateGroupMutation } from '../../../redux/services/group';
import { getDataGridReference } from '../../../services/data-grid.service';
import { promiseToast, showError, showSuccess } from '../../../services/notification.service';
import { createGroupPatch } from '../../../utils/group-functions';
import { forceAssert } from '../../../utils/type-guards';
import { GroupsPermissions } from '../../../utils/user.permissions';
import type { ActiveRecordContextProps } from '../../active-record/active-record.component';
import CloseActiveItemButton from '../../common/close-active-item-button';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import { FormLoading } from '../../form/form-loading.component';
import { FormTitle } from '../../form/form-title.component';
import { getGroupFormSettings } from './group-form-settings';
import GroupMenu from './group-menu.component';

export interface GroupDetailsProps {
  groupId: number;
  isNewGroup?: boolean;
  title: string;
  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}

interface GroupDetailsRef {
  submitForm?: () => void;
}

const saveButton: DynamicFormButtonSetting = {
  name: 'save',
  text: i18next.t('save'),
  htmlType: 'submit',
  type: 'primary',
  variant: 'alternative',
};
const archieveButton: DynamicFormButtonSetting = {
  name: 'archive',
  text: i18next.t('archive'),
  htmlType: 'button',
  type: 'default',
  variant: 'alternative',
};

const restoreButton: DynamicFormButtonSetting = {
  name: 'restore',
  text: i18next.t('restore'),
  htmlType: 'button',
  type: 'default',
  variant: 'alternative',
  disabled: false,
};

const GroupDetails = (props: GroupDetailsProps, ref: Ref<GroupDetailsRef>) => {
  const { groupId, isNewGroup = false, title, onClose, panelSettings, onCloseActiveRecordConfirmationModalRef } = props;

  const [group, setGroup] = useState<GroupStructure>();
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>();
  const [formSettings, setFormSettings] = useState<ExtendedDynamicFormSettings>();
  const dispatch = useDispatch();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const saveModalReference = useRef<DialogRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const dynamicFormId = useRef(v4());
  const closeOnModalSaveRef = useRef(false);

  const { t } = useTranslation();

  const { data, isLoading, isFetching } = useGetGroupByIdQuery(groupId);
  const [updateGroup] = useUpdateGroupMutation();
  const log = useApiLogger();

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);

  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });

  const { readOnly, editAccess, setReadOnly } = useUserAccess({
    initialAccess: isNewGroup,
    permissions: {
      MODIFY: [GroupsPermissions.ROLES_MODIFY, GroupsPermissions.ROLES_CREATE],
      READ: GroupsPermissions.ROLES_READ,
    },
  });

  const onArchieveGroup = (archive: boolean) => {
    if (groupId) {
      promiseToast(
        updateGroup({ id: groupId, archived: archive })
          .then(() => {
            onClose(dynamicFormId.current);
            dataGridRef.current?.api.data.getDataOverwritePageCache();
            dispatch(groupApi.util.invalidateTags([{ type: 'Group', id: 'LIST' }]));
          })
          .catch(() => showErrorToast()),
        getArchiveRestoreMessages(archive)
      );
    }
  };

  useEffect(() => {
    if (data && !isFetching) {
      setGroup(cloneDeep(data));
      const newFormButtons: Array<DynamicFormButtonSetting> = [];
      if (data.original.archived) {
        newFormButtons.push(restoreButton);
        restoreButton.onClick = () => onArchieveGroup(false);
        restoreButton.disabled = !editAccess;
      } else {
        archieveButton.onClick = () => onArchieveGroup(true);
        newFormButtons.push(saveButton, archieveButton);
      }
      setFormButtons(newFormButtons);
    }
  }, [data, isFetching, editAccess]);

  useEffect(() => {
    if (group?.original.archived) {
      setReadOnly(true);
    }
  }, [group, setReadOnly]);

  useEffect(() => {
    setFormSettings(getGroupFormSettings(readOnly));
  }, [readOnly]);

  useEffect(() => {
    panelSettings?.pipe(takeUntil(destroySubject.current)).subscribe((settings) => {
      setPanelContextProps(settings);
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          dynamicFormReference.current?.submitForm(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const renderEditAccessElements = () => {
    if (editAccess) {
      return (
        <div className="group relative">
          {!panelContextProps?.fullScreen && (
            <IconButton icon="Ellipsis" iconSize={OdinIconSize.Small} classNames="mx-2 h-9 flex items-center" />
          )}
          <GroupMenu groupTitle={title} groupId={groupId} fullScreen={panelContextProps?.fullScreen} />
        </div>
      );
    }
    return;
  };

  useImperativeHandle(ref, () => ({
    submitForm: dynamicFormReference.current?.submitForm,
  }));

  const onFormSubmit = (data: JsonDataItem) => {
    if (groupId && group?.original) {
      const patchData = createGroupPatch(data, dynamicFormReference.current?.formDirtyFields() || {});

      const groupChanges = {
        ...patchData,
        id: group.id,
        caption: patchData?.caption ?? group.caption,
      };

      saveGroupData(groupChanges as GroupItem, (response) => {
        const convertedResponse = forceAssert<GroupMutationResponse>(response);
        if (convertedResponse?.data?.result) {
          hidePerformingSaveToast();
          showSaveToast();
        } else if (convertedResponse?.error) {
          hidePerformingSaveToast();
          showErrorToast();
          log('Failed to update group data', { error: convertedResponse.error });
        }
      });
    }
  };

  const saveGroupData = (groupData: GroupItem, successCallback: (response?: unknown) => void) => {
    showPerformingSaveToast();
    if (groupData.id) {
      updateGroup(groupData)
        .then(successCallback)
        .catch(() => showErrorToast());
    }
  };

  const onCloseGroupDetails = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      onClose(dynamicFormId.current);
    } else {
      saveModalReference.current?.show();
    }
  };

  const showSaveToast = () => {
    if (!panelContextProps?.batchSaveInProgress) {
      showSuccess(t('record-saved'));
    }
    notifySaveSucceeded(dynamicFormId.current);
    closeOnModalSaveRef.current = true;
    if (!groupId || closeOnModalSaveRef.current) {
      onClose(dynamicFormId.current);
    }
  };

  const showErrorToast = () => {
    if (!panelContextProps?.batchSaveInProgress) {
      showError(t('group-details-save-failed'));
    }
    notifySaveFailed(dynamicFormId.current);
  };

  const onSaveConfirm = () => onFormSubmit(group as JsonDataItem);
  const onSaveCancel = () => onClose(dynamicFormId.current);

  const onFieldChanged = debounce(() => notifyDirty(dynamicFormReference.current), 500);

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />
          {!isLoading && (
            <div className="flex min-w-min justify-end">
              {renderEditAccessElements()}
              <CloseActiveItemButton onClick={onCloseGroupDetails} />
            </div>
          )}
        </div>
      </div>
      <div className="admin-form-default">
        {!group && <FormLoading />}
        {group && formSettings && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={group}
            settings={formSettings}
            buttons={formButtons}
            buttonsLocation={0}
            readOnly={readOnly}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={onSaveConfirm}
        onClose={onSaveCancel}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
    </>
  );
};
export default forwardRef(GroupDetails);
